/**
 * Leaving the app locks it, and nothing readable is left behind in the
 * phone's app switcher.
 *
 *  - Auto-lock: the moment the app goes to the background (home gesture, app
 *    switcher, another app, screen off), it locks — coming back always asks
 *    for the passphrase/passkey, whether from a notification, the app list or
 *    the Home Screen icon.
 *  - Privacy cover: the app switcher shows a screenshot of the app as you left
 *    it. As soon as the app starts losing focus the chats are hidden behind a
 *    plain TrueWave screen, so that screenshot shows the logo, not a message.
 *
 *  - Blur (phones), in two strengths:
 *      "hold" (default): chats are readable only while a finger is on the
 *        screen. iOS gives a web app no warning before it takes the
 *        app-switcher snapshot, so this is the only setting that can
 *        guarantee the snapshot never shows a message: by the time the swipe
 *        starts, the finger is off and the chat is blurred again.
 *      "until-leave": a touch & hold (HOLD_TO_UNBLUR_MS) unblurs until the
 *        app is left. More comfortable to read, but a chat left unblurred can
 *        appear in the app switcher.
 *
 * Exceptions: a call in progress (locking would hide the hang-up button), and
 * the phone's own photo/file picker opened from a chat (it briefly takes the
 * app off screen, and locking then would lose the attachment being picked).
 */
import { callInProgress } from "./state/call.js";
import { useSession } from "./state/session.js";

let autoLockSuspendedUntil = 0;

/** Don't auto-lock for a moment — e.g. while the photo/file picker is open. */
export function suspendAutoLock(ms = 2 * 60 * 1000): void {
  autoLockSuspendedUntil = Date.now() + ms;
}

export function resumeAutoLock(): void {
  autoLockSuspendedUntil = 0;
}

function setCover(on: boolean): void {
  document.documentElement.classList.toggle("tw-privacy", on);
}

function autoLock(): void {
  if (Date.now() < autoLockSuspendedUntil) return;
  // Includes a ringing incoming call: locking that away hid the Accept button
  // while the phone kept ringing, and left the call stuck afterwards.
  if (callInProgress()) return;
  if (useSession.getState().phase === "ready") useSession.getState().lock();
}

function leaving(): void {
  // Cover first, synchronously, so it's what the app-switcher snapshot shows.
  if (Date.now() >= autoLockSuspendedUntil) setCover(true);
  reblur();
  autoLock();
}

function back(): void {
  setCover(false);
}

/** Phones and tablets: where there's an app switcher to peek into. */
const touchDevice = window.matchMedia("(pointer: coarse)").matches;

const BLUR_KEY = "tw-blur-until-touched";

/** How chats are protected from a passing glance / the app switcher. */
export type BlurMode = "hold" | "until-leave" | "off";

/** Only offered on touch devices (there's no app switcher to peek into elsewhere). */
export function blurModeAvailable(): boolean {
  return touchDevice;
}

export function getBlurMode(): BlurMode {
  if (!touchDevice) return "off";
  try {
    const stored = localStorage.getItem(BLUR_KEY);
    if (stored === "off" || stored === "until-leave" || stored === "hold") return stored;
    // "on" was the old value for what is now "until-leave".
    if (stored === "on") return "until-leave";
  } catch {
    /* fall through to the default */
  }
  return "hold";
}

export function isBlurModeOn(): boolean {
  return getBlurMode() !== "off";
}

export function setBlurMode(mode: BlurMode): void {
  try {
    localStorage.setItem(BLUR_KEY, mode);
  } catch {
    /* falls back to the default next launch */
  }
  if (mode !== "until-leave") reblur();
  document.documentElement.classList.toggle("tw-blur-mode", isBlurModeOn());
}

/** How long a touch must stay down to unblur until the app is left. */
const HOLD_TO_UNBLUR_MS = 400;
let latched = false;
let holdTimer: ReturnType<typeof setTimeout> | null = null;

function setRevealed(on: boolean): void {
  document.documentElement.classList.toggle("tw-revealed", on || latched);
}

/** Unblurred by a touch & hold, until the app is next left. */
export function isRevealLatched(): boolean {
  return latched;
}

function clearHold(): void {
  if (holdTimer) clearTimeout(holdTimer);
  holdTimer = null;
}

/** Leaving the app: chats are blurred again next time. */
function reblur(): void {
  clearHold();
  latched = false;
  setRevealed(false);
}

let stripHeight: number | null = null;
window.addEventListener("resize", () => {
  stripHeight = null; // rotation changes it
});

/** Height of the home-indicator strip (iPhones without a home button), or 0. */
function homeIndicatorHeight(): number {
  if (stripHeight !== null) return stripHeight;
  const probe = document.createElement("div");
  probe.style.cssText = "position:fixed;bottom:0;height:env(safe-area-inset-bottom);visibility:hidden;pointer-events:none";
  document.body.appendChild(probe);
  stripHeight = probe.getBoundingClientRect().height;
  probe.remove();
  return stripHeight;
}

/**
 * iOS takes the app-switcher snapshot the instant the home-bar swipe starts —
 * before it tells the page anything (blur / "hidden" arrive too late, so the
 * snapshot still showed the chat). The one earlier signal: the swipe begins
 * as a touch on the home-indicator strip, which the page does see before iOS
 * claims the gesture. Cover on that touch; uncover if it turns out to be an
 * ordinary tap (touchend), and stay covered if iOS took it over
 * (touchcancel) until the app is genuinely back.
 */
function installHomeBarGuard(): void {
  let edgeTouch = false;
  const inStrip = (y: number) => {
    const strip = homeIndicatorHeight();
    return strip > 0 && y >= window.innerHeight - Math.max(strip, 24);
  };
  const uncoverIfBack = () => {
    if (document.visibilityState === "visible" && document.hasFocus()) setCover(false);
  };
  window.addEventListener(
    "touchstart",
    (e) => {
      const t = e.touches[0];
      if (!t) return;
      if (!inStrip(t.clientY)) {
        // A finger on the app itself: readable while it stays there — and,
        // held long enough, until the app is left.
        setRevealed(true);
        clearHold();
        if (getBlurMode() === "until-leave") {
          holdTimer = setTimeout(() => {
            holdTimer = null;
            latched = true;
            setRevealed(true);
          }, HOLD_TO_UNBLUR_MS);
        }
        return;
      }
      setRevealed(false);
      if (Date.now() < autoLockSuspendedUntil) return;
      edgeTouch = true;
      setCover(true);
    },
    { capture: true, passive: true },
  );
  window.addEventListener(
    "touchend",
    (e) => {
      // Last finger up: blurred again at once — unless it was a touch & hold.
      if (e.touches.length === 0) {
        clearHold();
        setRevealed(false);
      }
      if (!edgeTouch) return;
      edgeTouch = false;
      setTimeout(uncoverIfBack, 120);
    },
    { capture: true, passive: true },
  );
  window.addEventListener(
    "touchcancel",
    () => {
      clearHold();
      setRevealed(false);
      if (!edgeTouch) return;
      edgeTouch = false;
      // Normally iOS has taken the gesture and we're off to the app switcher.
      // If we're still here and focused a moment later, it was something else.
      setTimeout(uncoverIfBack, 1500);
    },
    { capture: true, passive: true },
  );
}

export function installPrivacyGuards(): void {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") leaving();
    else back();
  });
  window.addEventListener("pagehide", leaving);
  window.addEventListener("pageshow", back);
  if (touchDevice) {
    // On a phone, losing focus is the first sign of heading to the app
    // switcher — earlier than "hidden", so the cover makes it into the
    // snapshot. (Not on computers: there it just means another window was
    // clicked, and blanking the chat would only get in the way.)
    window.addEventListener("blur", () => {
      if (Date.now() >= autoLockSuspendedUntil) setCover(true);
    });
    window.addEventListener("focus", back);
    document.documentElement.classList.toggle("tw-blur-mode", isBlurModeOn());
    // Heading to the app switcher / another app.
    window.addEventListener("blur", reblur);
    installHomeBarGuard();
  }
}
